import { useState, useMemo, useCallback, useRef } from 'react'
import { View, Text, useWindowDimensions } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useScreenInsets } from '@/lib/hooks/use-screen-insets'
import Animated from 'react-native-reanimated'
import { ChevronLeft, ChevronRight } from '@/components/ui/icons'
import { selection } from '@/lib/utils/haptics'
import { Pressable } from '@/components/ui/Pressable'
import { EmptyState } from '@/components/ui/EmptyState'
import { addCalendarDaysISO as addDaysISO } from '@/lib/utils/date-range-calendar'
import {
  datesInRangeISO,
  groupContiguousDates,
  indexHostCalendar,
  hostCalendarQueryWindow,
  firstReservedInRange,
} from '@/lib/utils/host-calendar'
import { Button } from '@/components/ui/Button'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { useVehicleUnavailableRanges } from '@/lib/hooks/use-booking'
import { useSetBlockedDates } from '@/lib/hooks/use-hosting-dashboard'
import { useSemanticColors, SPACING, INTERACTION } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { todayInBookingTimeZoneISO } from '@/lib/utils/dates'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { useEntrance } from '@/lib/hooks/use-entrance'

export default function HostingCalendarScreen() {
  const enter = useEntrance()
  const { vehicleId, title } = useLocalSearchParams<{ vehicleId: string; title?: string }>()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useScreenInsets()
  const { isDesktop } = useBreakpoint()
  const { width: windowWidth } = useWindowDimensions()
  // The grid gets whatever the screen can give it, aiming at the 44pt target
  // CLAUDE.md sets. Seven columns of 44 need 308pt of content width, so on a
  // 390pt phone each cell lands at 51 and clears the rule comfortably; on a
  // 320pt screen the arithmetic only allows 41 and no amount of hitSlop can
  // invent the missing pixels without stealing them from the neighbouring day.
  // 40 stays the floor so the digits do not collide.
  const gridWidth = windowWidth - SPACING.lg * 2
  const DAY_SIZE = isDesktop ? 56 : Math.max(40, Math.min(56, Math.floor(gridWidth / 7)))
  const DAY_CIRCLE = 36
  // A day cell is a square-ish box, not a column. The week rows still take a
  // share of the height the screen has left, but never grow past 1.25× the
  // cell's own width: uncapped they reached ~92pt on a 390pt phone, so a
  // 51×92 cell held nothing but a 20pt number floating in the middle of it and
  // the month read as a grid whose contents had failed to render.
  const DAY_ROW_MAX = Math.round(DAY_SIZE * 1.25)

  const today = todayInBookingTimeZoneISO()
  const [year, setYear] = useState(() => Number(today.slice(0, 4)))
  const [month, setMonth] = useState(() => Number(today.slice(5, 7)))
  const savingRef = useRef(false)
  const [selectionMessage, setSelectionMessage] = useState<string | null>(null)
  // Range selection with two taps (Apple-calendar style): the first tap sets
  // the start, the second completes the range, a third starts over. Zero
  // friction versus the old tap-each-day toggle, which made "block the 5th to
  // the 8th" into four separate taps and a button nobody understood.
  const [rangeStart, setRangeStart] = useState<string | null>(null)
  const [rangeEnd, setRangeEnd] = useState<string | null>(null)

  const startDate = `${year}-${String(month).padStart(2, '0')}-01`
  const daysInMonth = new Date(year, month, 0).getDate()
  const endDate = `${year}-${String(month).padStart(2, '0')}-${daysInMonth}`

  const queryWindow = hostCalendarQueryWindow(startDate, endDate, rangeStart, rangeEnd)
  const {
    ranges: unavailableRanges,
    isLoading: unavailableLoading,
    error: availabilityError,
    refetch,
  } = useVehicleUnavailableRanges(vehicleId, queryWindow.from, queryWindow.to)
  const { setBlockedDates, isPending } = useSetBlockedDates(vehicleId ?? '')

  const monthNames = t('calendar.months').split(',')
  const dayNames = t('calendar.days_short').split(',')

  const unavailableByDate = useMemo(() => indexHostCalendar(unavailableRanges), [unavailableRanges])

  const calendarGrid = useMemo(() => {
    const firstDayOfWeek = (new Date(year, month - 1, 1).getDay() + 6) % 7 // Monday=0
    const cells: Array<{ day: number; date: string } | null> = []
    for (let i = 0; i < firstDayOfWeek; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++) {
      const date = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      cells.push({ day: d, date })
    }
    while (cells.length % 7 !== 0) cells.push(null)
    return cells
  }, [year, month, daysInMonth])

  // Weeks, not one wrapping row: each week is a flex row, so the grid takes
  // the height the screen has left instead of ending at a fixed 6 × 48pt —
  // up to the per-row ceiling above, which keeps a cell a cell.
  const calendarWeeks = useMemo(() => {
    const weeks: Array<typeof calendarGrid> = []
    for (let i = 0; i < calendarGrid.length; i += 7) {
      weeks.push(calendarGrid.slice(i, i + 7))
    }
    return weeks
  }, [calendarGrid])

  const tomorrow = useMemo(() => addDaysISO(today, 1), [today])

  // The concrete set of selected dates, derived from the range. A single tap
  // (start only) selects one day; a second tap completes the inclusive range.
  const selectedDates = useMemo(() => {
    if (!rangeStart) return new Set<string>()
    if (!rangeEnd) return new Set<string>([rangeStart])
    return new Set(datesInRangeISO(rangeStart, rangeEnd))
  }, [rangeStart, rangeEnd])

  // Civil dates must not shift when the host's phone is in another timezone.
  const formatDay = (iso: string) =>
    `${Number(iso.slice(8, 10))} ${monthNames[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`
  const selectedRangeLabel = rangeStart
    ? !rangeEnd || rangeEnd === rangeStart
      ? formatDay(rangeStart)
      : `${formatDay(rangeStart)} – ${formatDay(rangeEnd)}`
    : null

  const handleDayPress = useCallback(
    (date: string) => {
      if (savingRef.current || unavailableLoading || availabilityError) return
      setSelectionMessage(null)
      if (!rangeStart || rangeEnd || date < rangeStart) {
        setRangeStart(date)
        setRangeEnd(null)
      } else {
        const reserved = firstReservedInRange(rangeStart, date, unavailableByDate)
        if (reserved) {
          // Never silently shorten the range the host asked for.
          setSelectionMessage(t('calendar.crosses_reservation'))
          return
        }
        setRangeEnd(date)
      }
      void selection()
    },
    [rangeStart, rangeEnd, unavailableByDate, unavailableLoading, availabilityError, t],
  )

  const handlePrevMonth = () => {
    if (month === 1) {
      setMonth(12)
      setYear(year - 1)
    } else setMonth(month - 1)
  }

  const handleNextMonth = () => {
    if (month === 12) {
      setMonth(1)
      setYear(year + 1)
    } else setMonth(month + 1)
  }

  // One adaptive action: if every selected day is already blocked the button
  // unblocks; otherwise it blocks. A single button, no guessing about which of
  // two to press, and a mixed range resolves to "block" (normalises the window).
  const selectedAllBlocked = useMemo(() => {
    if (selectedDates.size === 0) return false
    for (const d of selectedDates) {
      if (unavailableByDate.get(d) !== 'blocked') return false
    }
    return true
  }, [selectedDates, unavailableByDate])

  const selectedHasBlocked = [...selectedDates].some(
    (date) => unavailableByDate.get(date) === 'blocked',
  )
  const selectionHasReservation = [...selectedDates].some(
    (date) => unavailableByDate.get(date) === 'reserved',
  )
  const canApply =
    !!vehicleId &&
    !unavailableLoading &&
    !availabilityError &&
    !isPending &&
    !selectionHasReservation &&
    !!rangeStart &&
    rangeStart >= today

  const applyBlockedRange = async (block: boolean) => {
    if (!canApply || savingRef.current) return
    savingRef.current = true
    setSelectionMessage(null)
    // A contiguous selection is ONE atomic RPC. Never split around a new
    // reservation and tell the host their entire requested window was saved.
    const [range] = groupContiguousDates([...selectedDates])
    if (!range) {
      savingRef.current = false
      return
    }
    try {
      await setBlockedDates({
        windowStart: range.startDate,
        windowEnd: range.endDate,
        blockedRanges: block ? [{ ...range, reason: 'manual' }] : [],
      })
      toast.success(block ? t('calendar.dates_blocked') : t('calendar.dates_unblocked'))
      setSelectionMessage(
        t(block ? 'calendar.saved_block' : 'calendar.saved_unblock', { range: selectedRangeLabel }),
      )
      setRangeStart(null)
      setRangeEnd(null)
    } catch (err) {
      const message = block
        ? errorMessage(err, t('calendar.block_failed'))
        : errorMessage(err, t('calendar.unblock_failed'))
      setSelectionMessage(message)
      toast.error(message)
      captureError(err, {
        screen: 'hosting/calendar',
        action: block ? 'block-dates' : 'unblock-dates',
        vehicleId,
      })
    } finally {
      savingRef.current = false
    }
  }

  return (
    <ScreenContainer scroll={false} card={false}>
      <ScreenHeader title={t('calendar.title')} subtitle={title} />
      {availabilityError || !vehicleId ? (
        <EmptyState
          title={t('calendar.load_failed')}
          description={t('calendar.load_failed_body')}
          action={{ label: t('common.retry'), onPress: () => void refetch() }}
        />
      ) : (
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            flexGrow: 1,
            paddingHorizontal: SPACING.lg,
            paddingBottom: SPACING.lg,
          }}
          keyboardShouldPersistTaps="handled"
        >
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 14,
              color: colors.textSecondary,
              marginBottom: SPACING.md,
            }}
            testID="calendar.instructions"
          >
            {t('calendar.instructions')}
          </Text>
          {/* Month nav */}
          <Animated.View
            collapsable={false}
            entering={enter(1)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: SPACING.md,
            }}
          >
            <Pressable
              onPress={handlePrevMonth}
              disabled={isPending || startDate.slice(0, 7) <= today.slice(0, 7)}
              accessibilityRole="button"
              accessibilityLabel={t('calendar.previous_month')}
              testID="calendar.prev-month"
              // 40pt box → 56pt target without reflowing the month header.
              hitSlop={INTERACTION.iconHitSlop}
              style={{ width: 44, height: 44, alignItems: 'center', justifyContent: 'center' }}
            >
              <ChevronLeft size={22} color={colors.textPrimary} strokeWidth={2} />
            </Pressable>
            <Text style={{ fontSize: 18, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
              {monthNames[month - 1]} {year}
            </Text>
            <Pressable
              onPress={handleNextMonth}
              disabled={isPending}
              accessibilityRole="button"
              accessibilityLabel={t('calendar.next_month')}
              testID="calendar.next-month"
              hitSlop={INTERACTION.iconHitSlop}
              style={{ width: 44, height: 44, alignItems: 'center', justifyContent: 'center' }}
            >
              <ChevronRight size={22} color={colors.textPrimary} strokeWidth={2} />
            </Pressable>
          </Animated.View>

          {/* Day headers — fixed 7 × DAY_SIZE cells, centered. Prevents
            the percentage-based wrap glitch on wide desktops. */}
          <View
            style={{
              flexDirection: 'row',
              marginBottom: SPACING.xs,
              width: DAY_SIZE * 7,
              alignSelf: 'center',
            }}
          >
            {dayNames.map((d, i) => (
              <View key={i} style={{ width: DAY_SIZE, alignItems: 'center' }}>
                <Text style={{ fontSize: 12, fontFamily: FONTS.bodyBold, color: colors.textMuted }}>
                  {d}
                </Text>
              </View>
            ))}
          </View>

          {/* Grid — takes every point the screen has left */}
          <Animated.View collapsable={false} entering={enter(2)} style={{ flex: 1 }}>
            <View
              testID="calendar.month-view"
              style={{
                flex: 1,
                width: DAY_SIZE * 7,
                alignSelf: 'center',
              }}
            >
              {calendarWeeks.map((week, weekIndex) => (
                <View
                  key={`week-${weekIndex}`}
                  style={{
                    flexDirection: 'row',
                    flex: 1,
                    minHeight: DAY_SIZE,
                    maxHeight: DAY_ROW_MAX,
                  }}
                >
                  {week.map((cell, dayIndex) => {
                    if (!cell)
                      return (
                        <View key={`empty-${weekIndex}-${dayIndex}`} style={{ width: DAY_SIZE }} />
                      )

                    const unavailableReason = unavailableByDate.get(cell.date)
                    const isReserved = unavailableReason === 'reserved'
                    const isBlocked = unavailableReason === 'blocked'
                    const isSelected = selectedDates.has(cell.date)
                    const isRangeStart = rangeStart === cell.date
                    const isRangeEnd = rangeEnd === cell.date
                    const isRangeEndpoint = isRangeStart || isRangeEnd
                    const isSingle = isRangeStart && !rangeEnd
                    const isToday = cell.date === today
                    const isPast = cell.date < today
                    const isDisabled = isPast || isReserved || unavailableLoading || isPending

                    return (
                      <Pressable
                        key={cell.date}
                        onPress={() => !isDisabled && handleDayPress(cell.date)}
                        disabled={isDisabled}
                        accessibilityRole="button"
                        accessibilityLabel={`${formatDay(cell.date)}, ${t(isReserved ? 'calendar.reserved' : isBlocked ? 'calendar.blocked' : 'calendar.available')}`}
                        accessibilityState={{ disabled: isDisabled, selected: isSelected }}
                        testID={
                          cell.date === today
                            ? 'calendar.today'
                            : cell.date === tomorrow
                              ? 'calendar.tomorrow'
                              : `calendar.day-${cell.date}`
                        }
                        // The touchable IS the grid cell, with the 36pt circle drawn
                        // inside it. It used to be the circle plus a hitSlop that
                        // grew it to the cell — which works on native and does
                        // nothing on web, where react-native-web drops hitSlop
                        // entirely. Measured there: a 36x36 target.
                        style={{
                          width: DAY_SIZE,
                          alignItems: 'center',
                          justifyContent: 'center',
                          // Selected middle days get a light band so a multi-day
                          // selection reads as one continuous block, not two dots.
                          backgroundColor:
                            isSelected && !isRangeEndpoint && !isSingle
                              ? `${COLORS.coral.DEFAULT}2E`
                              : 'transparent',
                        }}
                      >
                        <View
                          style={{
                            width: DAY_CIRCLE,
                            height: DAY_CIRCLE,
                            borderRadius: DAY_CIRCLE / 2,
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isSelected
                              ? COLORS.coral.DEFAULT
                              : isReserved
                                ? `${COLORS.purple.DEFAULT}18`
                                : isBlocked
                                  ? `${COLORS.coral.DEFAULT}20`
                                  : 'transparent',
                            // Today keeps a neutral ring so it is marked without
                            // borrowing the coral/purple the key spends on
                            // availability. Its own state still wins the ring.
                            borderWidth:
                              (isBlocked || isReserved || isToday) && !isSelected ? 1 : 0,
                            borderColor: isReserved
                              ? COLORS.purple.DEFAULT
                              : isBlocked
                                ? COLORS.coral.DEFAULT
                                : colors.textPrimary,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 14,
                              fontFamily:
                                isBlocked || isSelected || isToday ? FONTS.bodyBold : FONTS.body,
                              color: isSelected
                                ? '#FFFFFF'
                                : isDisabled && !isReserved
                                  ? colors.textMuted
                                  : isReserved
                                    ? COLORS.purple.DEFAULT
                                    : isBlocked
                                      ? COLORS.coral.DEFAULT
                                      : colors.textPrimary,
                            }}
                          >
                            {cell.day}
                          </Text>
                        </View>
                      </Pressable>
                    )
                  })}
                </View>
              ))}
            </View>
          </Animated.View>
        </ScrollView>
      )}

      {/* Pinned footer: the key, and the actions the key describes. Both used
          to sit at the end of the scroll with 325pt of empty background under
          them. */}
      <View
        style={{
          gap: SPACING.md,
          paddingHorizontal: SPACING.lg,
          paddingTop: SPACING.md,
          paddingBottom: insets.bottom + SPACING.md,
          backgroundColor: colors.background,
          borderTopWidth: 1,
          borderTopColor: colors.border,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            gap: SPACING.lg,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {/* Each swatch is the day it stands for: an available day is a bare
              number, so its key is an empty ring, not the solid near-black dot
              that used to read as "selected". The ring is drawn in `textMuted`
              rather than `border` — a 1pt hairline of #E5E5EA on the #F5F5F7
              page is ~1.1:1 and simply is not there, while its two coloured
              siblings are, so the first key read as a broken swatch. */}
          <LegendSwatch
            label={t('calendar.available')}
            borderColor={colors.textMuted}
            colors={colors}
          />
          <LegendSwatch
            label={t('calendar.blocked')}
            fill={`${COLORS.coral.DEFAULT}20`}
            borderColor={COLORS.coral.DEFAULT}
            colors={colors}
          />
          <LegendSwatch
            label={t('calendar.reserved')}
            fill={`${COLORS.purple.DEFAULT}18`}
            borderColor={COLORS.purple.DEFAULT}
            colors={colors}
          />
        </View>

        {selectionMessage || selectionHasReservation ? (
          <Text
            accessibilityLiveRegion="polite"
            testID="calendar.feedback"
            style={{
              fontFamily: FONTS.body,
              fontSize: 14,
              color: selectionHasReservation ? colors.textError : colors.textSecondary,
              textAlign: 'center',
            }}
          >
            {selectionHasReservation ? t('calendar.crosses_reservation') : selectionMessage}
          </Text>
        ) : null}
        {selectedDates.size > 0 && selectedRangeLabel ? (
          <View style={{ gap: SPACING.md }}>
            <Text
              style={{
                textAlign: 'center',
                fontSize: 15,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
            >
              {selectedAllBlocked
                ? t('calendar.unblock_summary', { range: selectedRangeLabel })
                : t('calendar.block_summary', { range: selectedRangeLabel })}
            </Text>
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 13,
                color: colors.textSecondary,
                textAlign: 'center',
              }}
            >
              {t('calendar.whole_days', { count: selectedDates.size })}
            </Text>
            <Button
              onPress={() => applyBlockedRange(!selectedAllBlocked)}
              variant={selectedAllBlocked ? 'secondary' : 'primary'}
              size="lg"
              title={selectedAllBlocked ? t('calendar.unblock_dates') : t('calendar.block_dates')}
              loading={isPending}
              disabled={!canApply}
              testID={selectedAllBlocked ? 'calendar.unblock' : 'calendar.block'}
            />
            {selectedHasBlocked && !selectedAllBlocked ? (
              <Button
                title={t('calendar.unblock_dates')}
                variant="secondary"
                size="sm"
                disabled={!canApply}
                onPress={() => applyBlockedRange(false)}
                testID="calendar.unblock"
              />
            ) : null}
            <Button
              title={t('calendar.clear_selection')}
              variant="text"
              size="sm"
              disabled={isPending}
              onPress={() => {
                setRangeStart(null)
                setRangeEnd(null)
                setSelectionMessage(null)
              }}
              testID="calendar.clear-selection"
            />
          </View>
        ) : !availabilityError ? (
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 14,
              color: colors.textSecondary,
              textAlign: 'center',
            }}
          >
            {t(unavailableLoading ? 'common.loading' : 'calendar.select_dates')}
          </Text>
        ) : null}
      </View>
    </ScreenContainer>
  )
}

/** One key entry, drawn exactly the way the day it describes is drawn. */
function LegendSwatch({
  label,
  fill,
  borderColor,
  colors,
}: {
  label: string
  fill?: string
  borderColor: string
  colors: ReturnType<typeof useSemanticColors>
}) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
      <View
        style={{
          width: 12,
          height: 12,
          borderRadius: 6,
          backgroundColor: fill ?? 'transparent',
          borderWidth: 1,
          borderColor,
        }}
      />
      <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textSecondary }}>
        {label}
      </Text>
    </View>
  )
}
